import { tv } from 'tailwind-variants';

import { cn } from '../../../utils/tailwind.utils';

import {
  Icon,
  Icons,
  Size,
  TextIconColor,
  Typography
} from '~/components/core';
import {
  TypographyFont,
  TypographySize,
  TypographyWeight,
  TypographyWrap
} from '~/components/core/Typography';

/**
 * Styles for the select option component
 */
const selectOptionStyles = tv({
  base: 'hover:bg-primary-modal-bg relative flex w-full cursor-pointer select-none items-center gap-2 px-2 py-1 transition-all',
  variants: {
    isDestructive: {
      true: 'hover:bg-red-100'
    },
    isDisabled: {
      true: 'cursor-not-allowed opacity-50'
    },
    isSelected: {
      true: 'bg-primary-bright hover:bg-primary-bright-hover'
    }
  }
});

export interface SelectOptionProps<Value extends string | null = string> {
  destructive: boolean;
  disabled: boolean;
  isSelected: boolean;
  label: string;
  onClick: (value: Value) => void;
  value: Value;
  description?: string | undefined;
  endIcon?: { icon: Icon; color?: TextIconColor; size?: Size } | undefined;
  prefix?: string;
  startIcon?: { icon: Icon; color?: TextIconColor; size?: Size } | undefined;
  suffix?: { text: string; color?: TextIconColor; size?: TypographySize };
}

export const SelectOption = <Value extends string | null = string>({
  label,
  onClick,
  value,
  isSelected,
  disabled,
  startIcon,
  endIcon,
  prefix,
  suffix,
  destructive,
  description
}: SelectOptionProps<Value>) => {
  const iconAndTextColor = destructive
    ? TextIconColor.DESTRUCTIVE
    : TextIconColor.PRIMARY;

  const optionClasses = selectOptionStyles({
    isDestructive: destructive,
    isDisabled: disabled,
    isSelected
  });

  return (
    <li
      className={optionClasses}
      data-testid={`select-option-${value}`}
      data-value={value}
      onClick={() => !disabled && onClick(value)}
      role="menuitem"
    >
      <div className="flex w-full flex-row justify-between">
        <div className="flex w-full flex-row items-center justify-between gap-2">
          <div className="flex flex-col justify-start">
            <div className="flex flex-row items-center justify-start">
              {!!prefix && (
                <Typography
                  color={iconAndTextColor}
                  font={TypographyFont.INTERACTIVE}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.MEDIUM}
                >
                  {prefix}
                </Typography>
              )}
              {!!startIcon && (
                <Icons
                  color={startIcon.color}
                  icon={startIcon.icon}
                  size={startIcon.size}
                />
              )}
              <div className={cn(!!startIcon && 'ml-2')}>
                <Typography
                  color={iconAndTextColor}
                  font={TypographyFont.INTERACTIVE}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.MEDIUM}
                >
                  {label}
                </Typography>
              </div>
            </div>
            {description && (
              <Typography
                color={TextIconColor.SECONDARY}
                size={TypographySize.X_SMALL}
                weight={TypographyWeight.NORMAL}
              >
                {description}
              </Typography>
            )}
          </div>
        </div>
        <div className="flex flex-row items-center justify-start gap-1 space-x-1 pl-5">
          {!!suffix && (
            <Typography
              color={suffix.color ? suffix.color : TextIconColor.PRIMARY}
              size={suffix.size ? suffix.size : TypographySize.SMALL}
              wrap={TypographyWrap.NO_WRAP}
            >
              {suffix.text}
            </Typography>
          )}
          {!!endIcon && (
            <Icons
              color={endIcon.color}
              icon={endIcon.icon}
              size={endIcon.size}
            />
          )}
        </div>
      </div>
    </li>
  );
};

export default SelectOption;
